import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { ClaimBadge } from '@/components/StatusBadge';
import { percent, periodLabel, shortDate, won } from '@/lib/format';
import { useClaims, useCreateClaim } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 기성 청구.
 *
 * <p>협력사는 <b>제출(`CLAIMED`)까지</b>다. 검수(`APPROVED`)와 지급(`RECEIVED`)은 본사 화면에서만
 * 움직인다(DOC-82 §4-2). 여기서 넣은 숫자가 그대로 본사 원가 시트의 그 달 하도급 기성으로
 * 읽히기 때문에(`OfficeCostLedgerMapper.findSubcontractByMonth`), 검수 없이 확정되면 안 된다.
 */
export function ClaimsPage() {
  const { siteId, siteName, readOnly } = useSite();
  const claims = useClaims(siteId);
  const create = useCreateClaim(siteId);

  const [open, setOpen] = useState(false);
  const [periodYm, setPeriodYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const rows = claims.data ?? [];

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      periodYm,
      claimAmount: Number(amount.replace(/[^0-9]/g, '')),
      note: note.trim() === '' ? null : note.trim(),
    });
    setOpen(false);
    setAmount('');
    setNote('');
  }

  return (
    <>
      <PageHeader
        title="기성 청구"
        subtitle={siteName}
        action={
          !readOnly && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              청구
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>대상월</span>
            <input type="month" className="input-field" value={periodYm} onChange={(e) => setPeriodYm(e.target.value)} required />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>청구금액</span>
            <input
              type="text"
              inputMode="numeric"
              className="input-field"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              required
            />
          </label>
          <label className="mb-4 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>비고</span>
            <input type="text" className="input-field" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '제출'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              취소
            </button>
          </div>
        </form>
      )}

      <QueryState
        pending={claims.isPending}
        error={claims.isError}
        empty={rows.length === 0}
        emptyText="청구 내역이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows
            .slice()
            .sort((a, b) => b.claimSeq - a.claimSeq)
            .map((row) => (
              <li key={row.id} className="card">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-ui-md font-medium">
                      {row.claimSeq}회차 · {periodLabel(row.periodYm)}
                    </div>
                    <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      청구 {shortDate(row.claimDate)}
                      {row.receivedDate !== null && ` · 지급 ${shortDate(row.receivedDate)}`}
                    </div>
                  </div>
                  <ClaimBadge status={row.status} />
                </div>
                <div className="flex items-end justify-between gap-3">
                  <span className="text-ui-lg font-semibold">{won(row.claimAmount)}</span>
                  {row.claimRate !== null && (
                    <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      누계 {percent(row.claimRate)}
                    </span>
                  )}
                </div>
              </li>
            ))}
        </ul>
      </QueryState>
    </>
  );
}
